// Viewport hook for the layout decisions CSS cannot make.
//
// Most of the mobile work in this app is done in responsive.css, which
// overrides inline styles under a media query. That handles layout.
// It cannot handle behaviour — a sidebar that becomes an off-canvas
// drawer needs open/closed state, a scrim, and a button that did not
// exist on desktop. So the shell asks JS.
//
// 768px matches the breakpoint in responsive.css deliberately. Two
// different thresholds would mean a band of widths where the stylesheet
// thinks it is a phone and the shell thinks it is a desktop, which is
// exactly the kind of bug nobody reproduces on their own machine.

import { useEffect, useState } from "react";

export const MOBILE_BREAKPOINT = 768;

export function useIsMobile() {
  const query = `(max-width: ${MOBILE_BREAKPOINT}px)`;

  const [isMobile, setIsMobile] = useState(() => {
    // Guarded because a thumbnail renderer or an old browser may not
    // have matchMedia, and defaulting to desktop is the safer miss:
    // the layout degrades rather than hiding the navigation entirely.
    try {
      return window.matchMedia(query).matches;
    } catch (e) {
      return false;
    }
  });

  useEffect(() => {
    let mq;
    try {
      mq = window.matchMedia(query);
    } catch (e) {
      return undefined;
    }
    const onChange = (e) => setIsMobile(e.matches);
    setIsMobile(mq.matches);

    // addEventListener is the modern API; addListener is kept for
    // Safari versions that predate it, which is not a hypothetical
    // audience for an iPhone-installed PWA.
    if (mq.addEventListener) {
      mq.addEventListener("change", onChange);
      return () => mq.removeEventListener("change", onChange);
    }
    mq.addListener(onChange);
    return () => mq.removeListener(onChange);
  }, [query]);

  return isMobile;
}
